<template>
  <div>
    <v-dialog v-model="variation_dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon dark @click="variation_dialog = false">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
          <v-toolbar-title>Variations</v-toolbar-title>
        </v-toolbar>
        <div style="padding: 50px">
          <v-data-table :headers="variation_headers" :items="variations" :items-per-page="10" class="elevation-1 mt-4">
            <template v-slot:body="{ items }">
              <tbody v-if="!loading_variations">
                <tr
                  v-for="variant in items"
                  :key="variant.id"
                  :set="(variant_attributes = JSON.parse(variant.attributes))"
                >
                  <td>
                    <span
                      v-for="(variation_value, key, index) in variant_attributes.variations"
                      :key="`variation-value-${key}-${variant.id}`"
                    >
                      &nbsp; {{ index > 0 ? '-' : '' }} &nbsp; <strong>{{ key }}: </strong>{{ variation_value }}
                    </span>
                  </td>
                  <td>{{ variant.sku }}</td>
                  <td>{{ variant.price }}</td>
                  <td>
                    {{ variant.stock_quantity > 900 ? '900+' : variant.stock_quantity }}
                  </td>
                </tr>
              </tbody>
              <tbody v-else>
                <tr>
                  <td colspan="6" class="text-center" style="padding: 80px 0px">
                    <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="attribute_mapping_dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Attribute Mapping</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="attribute_mapping_dialog = false">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>
        <div style="padding: 50px">
          <v-data-table :headers="attribute_mapping_headers" :items="attribute_mapping" :items-per-page="10" class="elevation-1 mt-4">
            <template v-slot:body="{ items }">
              <tbody>
                <tr v-for="mapping in items" :key="mapping.id">
                  <td>{{ mapping.source }}</td>
                  <td>
                    <div v-if="mapping.property_details">
                      <strong>{{ getParsedPropertyDetails(mapping.property_details).name }}</strong>
                      <br />
                      <small>ID: {{ mapping.property_id }}</small>
                      <br />
                      <v-chip
                        v-if="getParsedPropertyDetails(mapping.property_details).is_required"
                        x-small
                        color="red"
                        text-color="white"
                        class="mt-1"
                      >
                        Required
                      </v-chip>
                      <v-chip
                        v-if="getParsedPropertyDetails(mapping.property_details).is_multivalued"
                        x-small
                        color="blue"
                        text-color="white"
                        class="mt-1 ml-1"
                      >
                        Multi-valued
                      </v-chip>
                    </div>
                    <div v-else>
                      ID: {{ mapping.property_id }}
                    </div>
                  </td>
                  <td>
                    <div v-if="mapping.value_details">
                      <v-chip
                        v-for="(value, index) in getParsedValueDetails(mapping.value_details)"
                        :key="index"
                        small
                        color="green"
                        text-color="white"
                        class="ma-1"
                      >
                        {{ value.name }}
                      </v-chip>
                    </div>
                    <div v-else-if="mapping.values">
                      <v-chip
                        v-for="(value, index) in mapping.values"
                        :key="index"
                        small
                        color="grey"
                        text-color="white"
                        class="ma-1"
                      >
                        {{ value }}
                      </v-chip>
                    </div>
                  </td>
                  <td>
                    <div v-if="mapping.scale || mapping.scale_id">
                      <div v-if="getScaleDisplayName(mapping)">
                        <strong>{{ getScaleDisplayName(mapping) }}</strong>
                        <br />
                        <small>ID: {{ mapping.scale_id || 'N/A' }}</small>
                      </div>
                      <div v-else>
                        <small class="text-muted">N/A</small>
                        <br />
                        <small>ID: {{ mapping.scale_id || 'N/A' }}</small>
                      </div>
                    </div>
                    <div v-else>
                      <small class="text-muted">N/A</small>
                    </div>
                  </td>
                  <td>
                    <small>{{ formatDate(mapping.created_at) }}</small>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-data-table>
        </div>
      </v-card>
    </v-dialog>

    <v-container>
      <v-card style="padding: 20px">
        <div v-if="is_feed_loading" class="text-center">
          <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="80px" />
        </div>
        <div v-else>
          <v-row>
            <v-col cols="3"> <strong>Profile Name: </strong> {{ feed.title }} </v-col>
            <v-col cols="6"></v-col>
            <v-col cols="2" class="text-center">
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs, value }">
                  <v-btn
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="initiating_upload"
                    small
                  >
                    <v-icon left>mdi-dots-vertical</v-icon>
                    Profile Actions
                    <v-icon right>{{ value ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item @click="reUploadProducts()" :disabled="initiating_upload">
                    <v-list-item-icon>
                      <v-icon color="info">mdi-cloud-upload</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-if="!initiating_upload">Re-upload {{ total_products }} Products</span>
                        <span v-else>Initiating Feed Upload...</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-list-item @click="forcedReUploadProducts" :disabled="initiating_upload">
                    <v-list-item-icon>
                      <v-icon color="red">mdi-backup-restore</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>
                        <span v-if="!initiating_upload">Force Re-upload Feed</span>
                        <span v-else>Initiating Feed Upload...</span>
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>

                  <v-divider></v-divider>

                  <v-list-item @click="viewAttributeMapping">
                    <v-list-item-icon>
                      <v-icon color="primary">mdi-map</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>View Attribute Mapping</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-col>
          </v-row>

          <v-divider class="mb-4 mt-4"></v-divider>
          <!-- <v-row>
            <v-col cols="6" sm="3" class="text-center">
              <h5 class="text-body-2 mb-1">Active Products</h5>
              <span style="font-size: 24px; font-weight: bold">{{ product_counts.active_count }}</span>
            </v-col>

            <v-col cols="6" sm="3" class="text-center">
              <h5 class="text-body-2 mb-1">Inactive Products</h5>
              <span style="font-size: 24px; font-weight: bold">{{ product_counts.inactive_count }}</span>
            </v-col>

            <v-col cols="6" sm="3" class="text-center">
              <h5 class="text-body-2 mb-1">Draft Products</h5>
              <span style="font-size: 24px; font-weight: bold">{{ product_counts.draft_count }}</span>
            </v-col>

            <v-col cols="6" sm="3" class="text-center">
              <h5 class="text-body-2 mb-1">Failed Products</h5>
              <span style="font-size: 24px; font-weight: bold">{{ product_counts.error_count }}</span>
            </v-col>
          </v-row> -->

          <!-- Collections Section -->
          <v-row v-if="has_collection && collections && collections.length > 0" class="mt-2">
            <v-col cols="12">
              <v-card outlined @click="toggleCollections" style="cursor: pointer">
                <v-card-title class="py-2">
                  <v-icon left small color="primary">{{ icons.mdiFolder }}</v-icon>
                  <span class="text-body-1">Collections ({{ collections_count }})</span>
                  <v-spacer></v-spacer>
                  <v-icon right small color="primary">{{ collectionsExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="py-2" v-show="collectionsExpanded">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th>Collection Name</th>
                          <th>Type</th>
                          <th>Products</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="collection in collections" :key="collection.id">
                          <td>
                            <strong>{{ collection.name || collection.title }}</strong>
                            <br />
                            <small class="grey--text">ID: {{ collection.remote_id }}</small>
                          </td>
                          <td>
                            <v-chip x-small :color="collection.type === 'custom' ? 'primary' : 'info'" text-color="white">
                              {{ collection.type === 'custom' ? 'Custom' : 'Smart' }}
                            </v-chip>
                          </td>
                          <td>{{ collection.tally || 0 }}</td>
                          <td>
                            <v-chip x-small :color="collection.submit_status == 1 ? 'success' : 'warning'" text-color="white">
                              {{ collection.submit_status == 1 ? 'Submitted' : 'Not Submitted' }}
                            </v-chip>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Profile Details Section -->
          <v-row class="mt-2">
            <v-col cols="12">
              <v-card outlined>
                <v-card-title @click="toggleProfileDetails" style="cursor: pointer" class="py-2">
                  <v-icon left small color="primary">{{ icons.mdiAccount }}</v-icon>
                  <span class="text-body-1 font-weight-medium">Profile Details</span>
                  <v-spacer></v-spacer>
                  <v-icon small>{{ profileDetailsExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                </v-card-title>
                <v-expand-transition>
                  <div v-show="profileDetailsExpanded">
                    <v-divider></v-divider>
                    <v-card-text class="py-2">
                      <!-- Etsy Configuration Dropdown -->
                      <div class="mb-3">
                        <v-card outlined class="mb-2">
                          <v-card-title @click.stop="etsyConfigurationExpanded = !etsyConfigurationExpanded" style="cursor: pointer" class="py-2">
                            <v-icon left small color="secondary">{{ icons.mdiShopping }}</v-icon>
                            <span class="text-body-2">Etsy Configuration</span>
                            <v-spacer></v-spacer>
                            <v-icon x-small>{{ etsyConfigurationExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                          </v-card-title>
                          <v-expand-transition>
                            <div v-show="etsyConfigurationExpanded">
                              <v-divider></v-divider>
                              <v-card-text class="py-2">
                                <!-- Profile Category Section -->
                                <div class="mb-3">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="profileCategoryExpanded = !profileCategoryExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiShape }}</v-icon>
                                      <span class="text-body-2">Etsy Category</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ profileCategoryExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="profileCategoryExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <v-row dense v-if="feed.remote_category">
                                            <v-col cols="12" md="6" v-if="feed.remote_category">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Category:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.remote_category }}</span>
                                              </p>
                                            </v-col>
                                          </v-row>
                                          <v-row dense v-else>
                                            <v-col cols="12">
                                              <p class="text-body-2">No category found</p>
                                            </v-col>
                                          </v-row>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>

                                <!-- Shipping Profile Section -->
                                <div class="mb-3">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="shippingProfileExpanded = !shippingProfileExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiTruck }}</v-icon>
                                      <span class="text-body-2">Etsy Shipping Profile</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ shippingProfileExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="shippingProfileExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <v-row dense v-if="feed.shipping_profile">
                                            <v-col cols="12" md="6" v-if="feed.shipping_profile.title">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Title:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.shipping_profile.title }}</span>
                                              </p>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="feed.shipping_profile.country">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Country:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.shipping_profile.country }}</span>
                                              </p>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="feed.shipping_profile.processing_days_display_label">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Processing Days:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.shipping_profile.processing_days_display_label }}</span>
                                              </p>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="feed.shipping_profile.shipping_template_id">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Shipping Template:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.shipping_profile.shipping_template_id }}</span>
                                              </p>
                                            </v-col>
                                          </v-row>
                                          <v-row dense v-else>
                                            <v-col cols="12">
                                              <p class="text-body-2">No shipping profile found</p>
                                            </v-col>
                                          </v-row>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>

                                <!-- Processing Profile Section -->
                                <div class="mb-3">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="processingProfileExpanded = !processingProfileExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiFactory }}</v-icon>
                                      <span class="text-body-2">Etsy Processing Profile</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ processingProfileExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="processingProfileExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <v-row dense v-if="feed.processing_profile">
                                            <v-col cols="12" md="6" v-if="feed.processing_profile.readiness_state">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Readiness State:</strong>
                                                <span class="ml-2 text-body-2">{{ formatSnakeCase(feed.processing_profile.readiness_state) }}</span>
                                              </p>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="feed.processing_profile.processing_days_display_label">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Processing Days:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.processing_profile.processing_days_display_label }}</span>
                                              </p>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="feed.processing_profile.readiness_state_id">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Readiness State ID:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.processing_profile.readiness_state_id }}</span>
                                              </p>
                                            </v-col>
                                          </v-row>
                                          <v-row dense v-else>
                                            <v-col cols="12">
                                              <p class="text-body-2">No processing profile found</p>
                                            </v-col>
                                          </v-row>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>

                                <!-- Production Partners Section -->
                                <div class="mb-3">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="productionPartnersExpanded = !productionPartnersExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiHandshake }}</v-icon>
                                      <span class="text-body-2">Production Partners</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ productionPartnersExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="productionPartnersExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <v-row dense v-if="feed.production_partners">
                                            <v-col cols="12" md="6" v-for="partner in feed.production_partners" :key="partner.id">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Partner Name:</strong>
                                                <span class="ml-2 text-body-2">{{ partner.partner_name }}</span>
                                              </p>
                                              <p class="mb-1">
                                                <strong class="text-body-2">Partner Location:</strong>
                                                <span class="ml-2 text-body-2">{{ partner.location }}</span>
                                              </p>
                                              <p class="mb-1">
                                                <strong class="text-body-2">Partner ID:</strong>
                                                <span class="ml-2 text-body-2">{{ partner.production_partner_id }}</span>
                                              </p>
                                              <v-divider></v-divider>
                                            </v-col>
                                          </v-row>
                                          <v-row dense v-else>
                                            <v-col cols="12">
                                              <p class="text-body-2">No production partners found</p>
                                            </v-col>
                                          </v-row>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>

                                <!-- Shop Section Section -->
                                <div class="mb-3">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="shopSectionExpanded = !shopSectionExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiVectorIntersection }}</v-icon>
                                      <span class="text-body-2">Etsy Shop Section</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ shopSectionExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="shopSectionExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <v-row dense v-if="feed.shop_section">
                                            <v-col cols="12" md="6" v-if="feed.shop_section.title">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Title:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.shop_section.title }}</span>
                                              </p>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="feed.shop_section.shop_section_id">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Shop Section ID:</strong>
                                                <span class="ml-2 text-body-2">{{ feed.shop_section.shop_section_id }}</span>
                                              </p>
                                            </v-col>
                                          </v-row>
                                          <v-row dense v-else>
                                            <v-col cols="12">
                                              <p class="text-body-2">No shop section found</p>
                                            </v-col>
                                          </v-row>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>
                              </v-card-text>
                            </div>
                          </v-expand-transition>
                        </v-card>
                      </div>

                      <!-- Category Attribute Mapping Dropdown -->
                      <div v-if="attribute_mapping && attribute_mapping.length > 0" class="mb-3">
                        <v-card outlined class="mb-2">
                          <v-card-title @click.stop="categoryAttributeMappingExpanded = !categoryAttributeMappingExpanded" style="cursor: pointer" class="py-2">
                            <v-icon left small color="primary">{{ icons.mdiMap }}</v-icon>
                            <span class="text-body-2">Category Attribute Mapping</span>
                            <v-spacer></v-spacer>
                            <v-chip x-small color="primary" text-color="white" class="mr-2">{{ attribute_mapping.length }}</v-chip>
                            <v-icon x-small>{{ categoryAttributeMappingExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                          </v-card-title>
                          <v-expand-transition>
                            <div v-show="categoryAttributeMappingExpanded">
                              <v-divider></v-divider>
                              <v-card-text class="py-2">
                                <v-simple-table dense>
                                  <template v-slot:default>
                                    <thead>
                                      <tr>
                                        <th>Source</th>
                                        <th>Property Details</th>
                                        <th>Value Details</th>
                                        <th>Scale</th>
                                        <th>Created At</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr v-for="mapping in attribute_mapping" :key="mapping.id">
                                        <td>{{ mapping.source }}</td>
                                        <td>
                                          <div v-if="mapping.property_details">
                                            <strong>{{ getParsedPropertyDetails(mapping.property_details).name }}</strong>
                                            <br />
                                            <small>ID: {{ mapping.property_id }}</small>
                                            <br />
                                            <v-chip
                                              v-if="getParsedPropertyDetails(mapping.property_details).is_required"
                                              x-small
                                              color="red"
                                              text-color="white"
                                              class="mt-1"
                                            >
                                              Required
                                            </v-chip>
                                            <v-chip
                                              v-if="getParsedPropertyDetails(mapping.property_details).is_multivalued"
                                              x-small
                                              color="blue"
                                              text-color="white"
                                              class="mt-1 ml-1"
                                            >
                                              Multi-valued
                                            </v-chip>
                                          </div>
                                          <div v-else>
                                            ID: {{ mapping.property_id }}
                                          </div>
                                        </td>
                                        <td>
                                          <div v-if="mapping.value_details">
                                            <v-chip
                                              v-for="(value, index) in getParsedValueDetails(mapping.value_details)"
                                              :key="index"
                                              small
                                              color="green"
                                              text-color="white"
                                              class="ma-1"
                                            >
                                              {{ value.name }}
                                            </v-chip>
                                          </div>
                                          <div v-else-if="mapping.values">
                                            <v-chip
                                              v-for="(value, index) in mapping.values"
                                              :key="index"
                                              small
                                              color="grey"
                                              text-color="white"
                                              class="ma-1"
                                            >
                                              {{ value }}
                                            </v-chip>
                                          </div>
                                        </td>
                                        <td>
                                          <div v-if="mapping.scale || mapping.scale_id">
                                            <div v-if="getScaleDisplayName(mapping)">
                                              <strong>{{ getScaleDisplayName(mapping) }}</strong>
                                              <br />
                                              <small>ID: {{ mapping.scale_id || 'N/A' }}</small>
                                            </div>
                                            <div v-else>
                                              <small class="text-muted">N/A</small>
                                              <br />
                                              <small>ID: {{ mapping.scale_id || 'N/A' }}</small>
                                            </div>
                                          </div>
                                          <div v-else>
                                            <small class="text-muted">N/A</small>
                                          </div>
                                        </td>
                                        <td>
                                          <small>{{ formatDate(mapping.created_at) }}</small>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </template>
                                </v-simple-table>
                              </v-card-text>
                            </div>
                          </v-expand-transition>
                        </v-card>
                      </div>

                      <!-- Mandatory product information for Etsy marketplace -->
                      <div v-if="has_profile_settings && profile_settings && (profile_settings.product_basic_info || profile_settings.listing_type_measurement)" class="mb-3">
                        <v-card outlined class="mb-2">
                          <v-card-title @click.stop="mandatoryProductInfoExpanded = !mandatoryProductInfoExpanded" style="cursor: pointer" class="py-2">
                            <v-icon left small color="secondary">{{ icons.mdiClipboardList }}</v-icon>
                            <span class="text-body-2">Mandatory product information for Etsy marketplace</span>
                            <v-spacer></v-spacer>
                            <v-icon x-small>{{ mandatoryProductInfoExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                          </v-card-title>
                          <v-expand-transition>
                            <div v-show="mandatoryProductInfoExpanded">
                              <v-divider></v-divider>
                              <v-card-text class="py-2">
                                <!-- Product Basic Info Dropdown -->
                                <div class="mb-3" v-if="profile_settings.product_basic_info">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="productBasicInfoExpanded = !productBasicInfoExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiCog }}</v-icon>
                                      <span class="text-body-2">Product Basic Info</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ productBasicInfoExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="productBasicInfoExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <p v-for="(value, key) in profile_settings.product_basic_info" :key="key" class="mb-1 text-body-2">
                                            <strong>{{ formatSettingKey(key) }}:</strong>
                                            <span class="ml-2">{{ formatSettingValue(value, key === 'is_supply' ? 'boolean' : 'string') }}</span>
                                          </p>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>

                                <!-- Listing Type and Measurement Options Dropdown -->
                                <div class="mb-3" v-if="profile_settings.listing_type_measurement">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="listingTypeMeasurementExpanded = !listingTypeMeasurementExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiTune }}</v-icon>
                                      <span class="text-body-2">Listing Type and Measurement Options</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ listingTypeMeasurementExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="listingTypeMeasurementExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <p v-if="profile_settings.listing_type_measurement.is_digital !== undefined" class="mb-1 text-body-2">
                                            <strong>Listing Type:</strong>
                                            <span class="ml-2">{{ formatListingTypeValue(profile_settings.listing_type_measurement.is_digital) }}</span>
                                          </p>
                                          <p v-if="profile_settings.listing_type_measurement.should_auto_renew !== undefined" class="mb-1 text-body-2">
                                            <strong>Renewal options:</strong>
                                            <span class="ml-2">{{ formatRenewalOptionsValue(profile_settings.listing_type_measurement.should_auto_renew) }}</span>
                                          </p>
                                          <p v-if="profile_settings.listing_type_measurement.weight_unit" class="mb-1 text-body-2">
                                            <strong>Weight Unit:</strong>
                                            <span class="ml-2">{{ formatWeightUnitValue(profile_settings.listing_type_measurement.weight_unit) }}</span>
                                          </p>
                                          <p v-if="profile_settings.listing_type_measurement.dimension_unit" class="mb-1 text-body-2">
                                            <strong>Dimension Unit:</strong>
                                            <span class="ml-2">{{ formatDimensionUnitValue(profile_settings.listing_type_measurement.dimension_unit) }}</span>
                                          </p>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>
                              </v-card-text>
                            </div>
                          </v-expand-transition>
                        </v-card>
                      </div>

                      <!-- Inventory Settings Dropdown -->
                      <div v-if="has_profile_settings && profile_settings" class="mb-3">
                        <v-card outlined class="mb-2">
                          <v-card-title @click.stop="inventorySettingsExpanded = !inventorySettingsExpanded" style="cursor: pointer" class="py-2">
                            <v-icon left small color="secondary">{{ icons.mdiPackageVariant }}</v-icon>
                            <span class="text-body-2">Inventory Settings</span>
                            <v-spacer></v-spacer>
                            <v-icon x-small>{{ inventorySettingsExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                          </v-card-title>
                          <v-expand-transition>
                            <div v-show="inventorySettingsExpanded">
                              <v-divider></v-divider>
                              <v-card-text class="py-2">
                                <p class="mb-1 text-body-2">
                                  <strong>Override inventory with fixed inventory for Etsy products:</strong>
                                  <span class="ml-2" v-if="profile_settings.custom_inventory !== undefined && profile_settings.custom_inventory !== null && profile_settings.custom_inventory !== ''">true</span>
                                  <span class="ml-2" v-else>false</span>
                                </p>
                                <p v-if="profile_settings.custom_inventory !== undefined && profile_settings.custom_inventory !== null && profile_settings.custom_inventory !== ''" class="mb-1 text-body-2">
                                  <strong>Value:</strong>
                                  <span class="ml-2">{{ profile_settings.custom_inventory }}</span>
                                </p>
                              </v-card-text>
                            </div>
                          </v-expand-transition>
                        </v-card>
                      </div>

                      <!-- Additional Settings Dropdown -->
                      <div v-if="profile_override || hasTitleOrDescriptionMapping || (has_profile_settings && profile_settings && profile_settings.sync_settings)" class="mb-3">
                        <v-card outlined class="mb-2">
                          <v-card-title @click.stop="additionalSettingsExpanded = !additionalSettingsExpanded" style="cursor: pointer" class="py-2">
                            <v-icon left small color="secondary">{{ icons.mdiCog }}</v-icon>
                            <span class="text-body-2">Additional Settings</span>
                            <v-spacer></v-spacer>
                            <v-icon x-small>{{ additionalSettingsExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                          </v-card-title>
                          <v-expand-transition>
                            <div v-show="additionalSettingsExpanded">
                              <v-divider></v-divider>
                              <v-card-text class="py-2">
                                <!-- Profile Overrides -->
                                <div v-if="profile_override" class="mb-3">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="profileOverridesExpanded = !profileOverridesExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="primary">{{ icons.mdiTune }}</v-icon>
                                      <span class="text-body-2">Profile Overrides</span>
                                      <v-spacer></v-spacer>
                                      <v-chip x-small color="primary" text-color="white" class="mr-2">
                                        {{ getProfileOverrideCount() }}
                                      </v-chip>
                                      <v-icon x-small>{{ profileOverridesExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="profileOverridesExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <div v-if="profile_override.has_title_overrides" class="mb-3">
                                            <div class="d-flex align-center mb-1">
                                              <v-icon left x-medium color="info">{{ icons.mdiFormatTitle }}</v-icon>
                                              <span class="text-body-2 font-weight-medium text-body-2">Title Overrides ({{ profile_override.title_overrides.length }})</span>
                                            </div>
                                            <div class="ml-4">
                                              <v-chip
                                                v-for="(override, index) in profile_override.title_overrides"
                                                :key="index"
                                                x-medium
                                                color="info"
                                                text-color="white"
                                                class="ma-1"
                                              >
                                                {{ override.human_readable }}
                                              </v-chip>
                                            </div>
                                          </div>
                                          <v-divider v-if="profile_override.has_title_overrides && profile_override.has_price_overrides" class="my-2"></v-divider>
                                          <div v-if="profile_override.has_price_overrides">
                                            <div class="d-flex align-center mb-1">
                                              <v-icon left x-medium color="success">{{ icons.mdiCurrencyUsd }}</v-icon>
                                              <span class="text-body-2 font-weight-medium text-body-2">Price Overrides ({{ profile_override.price_overrides.length }})</span>
                                            </div>
                                            <div class="ml-4">
                                              <v-chip
                                                v-for="(override, index) in profile_override.price_overrides"
                                                :key="index"
                                                x-medium
                                                color="success"
                                                text-color="white"
                                                class="ma-1"
                                              >
                                                {{ override.human_readable }}
                                              </v-chip>
                                            </div>
                                          </div>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>

                                <!-- Content Mappings -->
                                <div v-if="hasTitleOrDescriptionMapping" class="mb-3">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="contentMappingsExpanded = !contentMappingsExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiFileDocument }}</v-icon>
                                      <span class="text-body-2">Content Mappings</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ contentMappingsExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="contentMappingsExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <v-row dense>
                                            <v-col cols="12" md="6" v-if="advanced_fields.title_mapping">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Title Mapping:</strong>
                                                <span class="ml-2 text-body-2">{{ formatMapping(advanced_fields.title_mapping) }}</span>
                                              </p>
                                            </v-col>
                                            <v-col cols="12" md="6" v-if="advanced_fields.description_mapping">
                                              <p class="mb-1">
                                                <strong class="text-body-2">Description Mapping:</strong>
                                                <span class="ml-2 text-body-2">{{ formatMapping(advanced_fields.description_mapping) }}</span>
                                              </p>
                                            </v-col>
                                          </v-row>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>

                                <!-- Auto-Sync Product Data to Etsy -->
                                <div class="mb-3" v-if="has_profile_settings && profile_settings && profile_settings.sync_settings">
                                  <v-card outlined class="mb-2">
                                    <v-card-title @click.stop="autoSyncProductDataExpanded = !autoSyncProductDataExpanded" style="cursor: pointer" class="py-2">
                                      <v-icon left small color="secondary">{{ icons.mdiSync }}</v-icon>
                                      <span class="text-body-2">Auto-Sync Product Data to Etsy</span>
                                      <v-spacer></v-spacer>
                                      <v-icon x-small>{{ autoSyncProductDataExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                                    </v-card-title>
                                    <v-expand-transition>
                                      <div v-show="autoSyncProductDataExpanded">
                                        <v-divider></v-divider>
                                        <v-card-text class="py-2">
                                          <p v-for="(value, key) in profile_settings.sync_settings" :key="key" class="mb-1 text-body-2">
                                            <strong>{{ formatSettingKey(key) }}:</strong>
                                            <span class="ml-2">{{ formatSettingValue(value, 'boolean') }}</span>
                                          </p>
                                        </v-card-text>
                                      </div>
                                    </v-expand-transition>
                                  </v-card>
                                </div>
                              </v-card-text>
                            </div>
                          </v-expand-transition>
                        </v-card>
                      </div>

                      <!-- Advanced Configuration Dropdown -->
                      <div v-if="advanced_fields && hasAdvancedFields" class="mb-3">
                        <v-card outlined class="mb-2">
                          <v-card-title @click.stop="advancedFieldsExpanded = !advancedFieldsExpanded" style="cursor: pointer" class="py-2">
                            <v-icon left small color="secondary">{{ icons.mdiUfo }}</v-icon>
                            <span class="text-body-2">Advanced Configuration</span>
                            <v-spacer></v-spacer>
                            <v-icon x-small>{{ advancedFieldsExpanded ? icons.mdiChevronUp : icons.mdiChevronDown }}</v-icon>
                          </v-card-title>
                          <v-expand-transition>
                            <div v-show="advancedFieldsExpanded">
                              <v-divider></v-divider>
                              <v-card-text class="py-2">
                                <v-row dense>
                                  <template v-for="(value, key) in advanced_fields">
                                    <v-col
                                      v-if="value && key !== 'title_mapping' && key !== 'description_mapping'"
                                      :key="key"
                                      cols="12"
                                      md="6"
                                    >
                                      <p class="mb-1">
                                        <strong class="text-body-2">{{ formatAdvancedFieldName(key) }}:</strong>
                                        <span class="ml-2 text-body-2">{{ value }}</span>
                                      </p>
                                    </v-col>
                                  </template>
                                </v-row>
                              </v-card-text>
                            </div>
                          </v-expand-transition>
                        </v-card>
                      </div>
                    </v-card-text>
                  </div>
                </v-expand-transition>
              </v-card>
            </v-col>
          </v-row>
        </div>
      </v-card>

      <v-card class="elevation-1 mt-4 feed-products-card">
        <v-tabs v-model="productStateTabModel">
          <v-tabs-slider></v-tabs-slider>
          <v-tab
            v-for="tab in productStateTabs"
            :key="tab.value"
            :href="'#tab-' + tab.value"
          >
            <v-icon v-if="tab.value === 'failed'" x-small class="mr-1">{{ icons.mdiAlert }}</v-icon>
            {{ tab.label }} &nbsp;<span class="primary--text">{{ getProductCountForTab(tab.value) }}</span>
          </v-tab>
        </v-tabs>

        <v-data-table
          :headers="headers"
          :items="feed_products"
          :server-items-length="pagination.total"
          :options.sync="tableOptions"
          :loading="is_feed_product_loading"
          class="product-table"
        >
          <template v-slot:top>
            <div class="product-table-toolbar pa-4">
              <v-row align="center" justify="space-between">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search_keyword"
                  label="Search Profile Product"
                  outlined
                  dense
                  placeholder="Search Profile Product"
                  hide-details
                  clearable
                  class="product-search-field"
                ></v-text-field>
              </v-col>
              <v-col v-if="selectedProductIds.length > 0" cols="12" md="auto" class="d-flex align-center justify-end">
                <v-btn
                  color="primary"
                  :disabled="initiating_upload"
                  @click="reUploadSelectedProducts()"
                >
                  <v-icon left small>mdi-cloud-upload</v-icon>
                  Reupload {{ selectedProductIds.length }} product{{ selectedProductIds.length === 1 ? '' : 's' }}
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </template>
        <template v-slot:[`header.data-table-select`]>
          <v-simple-checkbox
            :value="isAllOnPageSelected"
            :indeterminate="isSomeOnPageSelected && !isAllOnPageSelected"
            @input="toggleSelectAllOnPage"
          ></v-simple-checkbox>
        </template>
        <template v-slot:body="{ items }">
          <tbody v-if="!is_feed_product_loading">
            <tr v-for="item in items" :key="item.id">
              <td class="checkbox-cell py-2">
                <v-simple-checkbox
                  :value="isProductSelected(item.id)"
                  @input="toggleProductSelection(item.id)"
                ></v-simple-checkbox>
              </td>
              <td class="pl-5 py-2"><img width="60px" :src="getProductImage(item.attributes)" alt="" /></td>
              <td>{{ item.title }}</td>
              <td>{{ item.sku }}</td>
              <td>
                <div :set="(etsy_product = item.etsy_product)">
                  <div v-if="!etsy_product">
                    <span class="dpl_badge dpl_badge_secondary">N/A</span>
                  </div>
                  <div v-else>
                    <!-- <span
                      v-if="etsy_product.etsy_state == 'upload-failed' || etsy_product.is_uploaded == 0"
                      class="dpl_badge dpl_badge_danger"
                      >Upload Failed</span
                    > -->
                    <v-chip
                      v-if="etsy_product.etsy_state == 'upload-failed' || etsy_product.is_uploaded == 0"
                      small
                      class="ma-2"
                      color="red"
                      text-color="white"
                      >Upload failed</v-chip
                    >
                    <div v-else>
                      <div v-if="etsy_product.etsy_state == 'draft'">
                        <!-- <span
                          class="product_draft-badge dpl_badge dpl_badge_warning dpl_mB-1"
                          style="display: lnline-block !important"
                          >Draft</span
                        > -->
                        <v-chip small class="ma-2" color="primary" text-color="white">Draft</v-chip>
                      </div>
                      <div v-if="etsy_product.etsy_state == 'active'">
                        <span class="dpl_badge dpl_badge_success-dark">Active</span>
                      </div>
                      <div v-if="etsy_product.etsy_state == 'edit' || etsy_product.etsy_state == 'inactive'">
                        <span class="dpl_badge dpl_badge_danger">Inactive</span>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
              <td v-html="renderUploadStatus(item)"></td>

              <td>
                  <div class="d-flex align-center" style="gap: 12px;">
                    <!-- Shopify Link -->
                    <a
                      v-if="item.shopify_handle"
                      :href="`https://${shop.specifier}/products/${item.shopify_handle}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View in Shopify"
                      class="d-inline-flex"
                    >
                      <img :src="require('@/assets/images/logos/shopify.png')" alt="View in Shopify" height="32" width="32" style="display: block; object-fit: contain;" />
                    </a>
                    <!-- Etsy Link -->
                    <a
                      v-if="getEtsyProductUrl(item)"
                      :href="getEtsyProductUrl(item)"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Edit on Etsy"
                      class="d-inline-flex"
                    >
                      <img :src="require('@/assets/images/logos/etsy.png')" alt="Edit on Etsy" height="32" width="32" style="display: block; object-fit: contain;" />
                    </a>
                  </div>
              </td>

              <td>
                <v-btn
                  title="Show Variations"
                  fab
                  @click="showVariations(item.id)"
                  small
                  v-if="item.parent_id == 0 && item.variant_id == 0"
                  class="table-action--button"
                >
                  <v-icon small dark>{{ icons.mdiChevronDown }}</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr>
              <td colspan="8" class="text-center" style="padding: 80px 0px">
                <img :src="require('@/assets/images/misc/loading.gif')" alt="" width="100px" />
              </td>
            </tr>
          </tbody>
        </template>
        </v-data-table>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import store from '@/store'
import {
  mdiChevronDown,
  mdiClose,
  mdiDotsVertical,
  mdiChevronUp,
  mdiTune,
  mdiFormatTitle,
  mdiCheckCircle,
  mdiCurrencyUsd,
  mdiMap,
  mdiCog,
  mdiFileDocument,
  mdiViewDashboard,
  mdiFolder,
  mdiAlert,
  mdiShape,
  mdiTruck,
  mdiFactory,
  mdiHandshake,
  mdiVectorIntersection,
  mdiAccount,
  mdiClipboardList,
  mdiSync,
  mdiPackageVariant,
  mdiShopping,
  mdiUfo
} from '@mdi/js'
export default {
  data() {
    return {
      feed_id: null,
      shop: null,
      feed: {},
      attribute_mapping: [],
      profile_override: null,
      advanced_fields: {},
      feed_products: [],
      has_collection: false,
      collections: [],
      collection_names: [],
      collections_count: 0,
      feedType: null,
      attributeMappingExpanded: false,
      contentMappingsExpanded: false,
      advancedFieldsExpanded: false,
      profileOverridesExpanded: false,
      profileDetailsExpanded: false,
      etsyConfigurationExpanded: false,
      categoryAttributeMappingExpanded: false,
      mandatoryProductInfoExpanded: false,
      productBasicInfoExpanded: false,
      listingTypeMeasurementExpanded: false,
      inventorySettingsExpanded: false,
      additionalSettingsExpanded: false,
      autoSyncProductDataExpanded: false,
      profileCategoryExpanded: false,
      shippingProfileExpanded: false,
      processingProfileExpanded: false,
      shopSectionExpanded: false,
      productionPartnersExpanded: false,
      collectionsExpanded: false,
      profile_settings: null,
      has_profile_settings: false,
      search_keyword: null,
      product_counts: {
        uploaded_count: 0,
        error_count: 0,
        active_count: 0,
        draft_count: 0,
        inactive_count: 0,
      },
      filters: {
        state: '',
      },
      productStateTab: 'all',
      productStateTabs: [
        { label: 'All', value: 'all' },
        { label: 'Active', value: 'active' },
        { label: 'Draft', value: 'draft' },
        { label: 'Inactive', value: 'inactive' },
        { label: 'Failed', value: 'failed' },
      ],
      selectedProductIds: [],
      is_feed_loading: false,
      is_feed_product_loading: false,
      total_products: null,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      pagination: {
        total: 0,
        last_page: 1,
      },
      headers: [
        { text: '', value: 'data-table-select', sortable: false, width: 48 },
        { text: 'Image', value: 'image', sortable: false },
        { text: 'Title', value: 'title', sortable: false },
        { text: 'SKU', value: 'sku', sortable: false },
        { text: 'Etsy Status', value: 'etsy_status', sortable: false },
        { text: 'Remarks', value: 'remarks', sortable: false },
        { text: 'View on', value: 'view_on', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],

      icons: {
        mdiChevronDown,
        mdiClose,
        mdiDotsVertical,
        mdiChevronUp,
        mdiTune,
        mdiFormatTitle,
        mdiCheckCircle,
        mdiCurrencyUsd,
        mdiMap,
        mdiCog,
        mdiFileDocument,
        mdiViewDashboard,
        mdiFolder,
        mdiAlert,
        mdiShape,
        mdiTruck,
        mdiFactory,
        mdiHandshake,
        mdiVectorIntersection,
        mdiAccount,
        mdiClipboardList,
        mdiSync,
        mdiPackageVariant,
        mdiShopping,
        mdiUfo,
      },

      variation_dialog: false,
      loading_variations: false,
      variations: [],
      variation_headers: [
        {
          text: 'Attributes',
        },
        { text: 'SKU' },
        { text: 'Price' },
        { text: 'Stock' },
      ],

      initiating_upload: false,

      attribute_mapping_dialog: false,
      attribute_mapping_headers: [
        {
          text: 'Source',
        },
        { text: 'Property Details' },
        { text: 'Value Details' },
        { text: 'Scale' },
        { text: 'Created At' },
      ],
    }
  },
  mounted() {
    this.shop = store.getters.loggedInShop
    this.feed_id = this.$route.params.feed_id
    this.getFeedDetails()
    this.getFeedProducts()
  },
  watch: {
    tableOptions: {
      handler(newVal, oldVal) {
        if (!oldVal) return

        if (newVal.page !== oldVal.page || newVal.itemsPerPage !== oldVal.itemsPerPage) {
          this.getFeedProducts()
        }
      },
      deep: true,
    },
    search_keyword: {
      handler(val) {
        this.debouncedSearch()
      },
    },
  },
  created() {
    this.debouncedSearch = require('lodash/debounce')(() => {
      this.tableOptions.page = 1
      this.getFeedProducts()
    }, 350)
  },
  methods: {
    async getFeedDetails() {
      try {
        this.is_feed_loading = true
        let response = await axios.get(`admin/shops/${this.shop.id}/feed/${this.feed_id}/get-details`)
        if (response.data.success) {
          this.feed = response.data.feed
          this.attribute_mapping = response.data.attribute_mappings || []
          this.profile_override = response.data.profile_override || null
          this.advanced_fields = response.data.advanced_fields || {}
          // Collection information
          this.feedType = response.data.type || response.data.feed?.type || (response.data.collection_type === 'collection' ? 'collection' : 'single')
          this.has_collection = response.data.has_collection || false
          this.collections = response.data.collections || []
          this.collection_names = response.data.collection_names || []
          this.collections_count = response.data.collections_count || 0
          // Settings information
          this.profile_settings = response.data.profile_settings || null
          this.has_profile_settings = response.data.has_profile_settings || false
        }
      } catch (e) {
        console.error('Error fetching feed details:', e)
      } finally {
        this.is_feed_loading = false
      }
    },

    getProductCountForTab(tabValue) {
      const counts = this.product_counts
      if (tabValue === 'all') return this.total_products ?? 0
      if (tabValue === 'active') return counts.active_count ?? 0
      if (tabValue === 'draft') return counts.draft_count ?? 0
      if (tabValue === 'inactive') return counts.inactive_count ?? 0
      if (tabValue === 'failed') return counts.error_count ?? 0

      return 0
    },

    setProductStateTab(tabValue) {
      this.productStateTab = tabValue
      this.filters.state = tabValue === 'all' ? '' : tabValue
      this.tableOptions.page = 1
      this.getFeedProducts()
    },


    toggleProductSelection(id) {
      const idx = this.selectedProductIds.indexOf(id)
      if (idx === -1) {
        this.selectedProductIds = [...this.selectedProductIds, id]
      } else {
        this.selectedProductIds = this.selectedProductIds.filter((x) => x !== id)
      }
    },

    isProductSelected(id) {
      return this.selectedProductIds.indexOf(id) !== -1
    },

    toggleSelectAllOnPage() {
      const items = this.feed_products || []
      const allSelected = items.every((item) => this.selectedProductIds.indexOf(item.id) !== -1)
      if (allSelected) {
        this.selectedProductIds = this.selectedProductIds.filter((id) => !items.some((i) => i.id === id))
      } else {
        const idsToAdd = items.map((i) => i.id).filter((id) => this.selectedProductIds.indexOf(id) === -1)
        this.selectedProductIds = [...this.selectedProductIds, ...idsToAdd]
      }
    },

    async getFeedProducts() {
      if (this.is_feed_product_loading) return
      try {
        this.is_feed_product_loading = true
        let url_params = {
          state: this.filters.state,
          page: this.tableOptions.page,
          per_page: this.tableOptions.itemsPerPage,
        }
        if (this.search_keyword && this.search_keyword.trim()) {
          url_params.search = this.search_keyword.trim()
        }
        let response = await axios.get(`admin/shops/${this.shop.id}/feed/${this.feed_id}/get-products`, {
          params: url_params,
        })

        if (response.data.success) {
          const paginatedData = response.data.products
          this.feed_products = paginatedData.data || []

          if (this.total_products == null) {
            this.total_products = paginatedData.total || 0
          }
          this.pagination.total = paginatedData.total || 0
          this.pagination.last_page = paginatedData.last_page || 1

          /* Keep "All" tab count in sync with products.total when fetching without state filter */
          if (this.filters.state === '') {
            this.total_products = paginatedData.total || 0
          }

          if (response.data.feed_product_counts) {
            this.product_counts = response.data.feed_product_counts
          }
        }
      } catch (e) {
        console.error('Error fetching feed products:', e)
      } finally {
        this.is_feed_product_loading = false
      }
    },

    getProductImage(attributes) {
      try {
        const parsedAttributes = JSON.parse(attributes);
        let img_url = '';

        // Fallback: first image
        if (!img_url && Array.isArray(parsedAttributes.images) && parsedAttributes.images[0]) {
          img_url = parsedAttributes.images[0].src;
        }

        // Fallback: single image_url property
        if (!img_url) {
          img_url = parsedAttributes?.image_url || '';
        }

        return img_url;
      } catch (e) {
        console.error('Error parsing product attributes:', e);
        return '';
      }
    },

    getEtsyProductUrl(item) {
      const etsyProduct = item?.etsy_product;

      if (!etsyProduct) return null;

      const raw = etsyProduct.upload_result;

      if (raw == null) return null;

      try {
        const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
        const url = parsed?.url;

        return typeof url === 'string' && url.length > 0 ? url : null;
      } catch (e) {
        return null;
      }
    },

    renderUploadStatus(product) {
      let etsy_product = product.etsy_product

      if (!etsy_product) {
        return `<span class='dpl_badge dpl_badge_secondary'>Not Uploaded</span>`
      }

      if (etsy_product.etsy_state == 'deleted') {
        return `<span class='dpl_error_text'>You previously deleted this product from Etsy.`
      } else if (etsy_product.etsy_state == 'upload-failed' || etsy_product.is_uploaded == 0) {
        return product.etsy_product.upload_error_message
      } else {
        return `<span class="ma-2 v-chip theme--light v-size--small green white--text"><span class="v-chip__content">Uploaded</span></span>`
      }
    },

    async showVariations(product_id) {
      try {
        this.variation_dialog = true
        this.loading_variations = true
        let response = await axios.get(`admin/shops/${this.shop.id}/shopify-product/${product_id}/variations`)
        if (response.data.success) {
          this.variations = response.data.variations
        }
      } catch (e) {
      } finally {
        this.loading_variations = false
      }
    },

    forcedReUploadProducts() {
      this.$swal
        .fire({
          title: 'Are you sure?',
          html: 'You want to force-upload the feed? <br> <br> All the products inside the feed will get affected',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes',
        })
        .then(result => {
          if (result.isConfirmed) this.reUploadProducts(true)
        })
    },

    reUploadSelectedProducts() {
      this.reUploadProducts(false, this.selectedProductIds)
    },

    async reUploadProducts(is_forced = false, product_ids = null) {
      try {
        this.initiating_upload = true
        const body = {
          shop_id: this.shop.id,
          is_forced: is_forced,
        }
        if (product_ids && product_ids.length > 0) {
          body.product_ids = product_ids
        }
        let response = await axios.post(`admin/shops/feed/${this.feed_id}/upload-products-to-etsy`, body)

        if (response.data.success) {
          this.$swal.fire({
            icon: 'success',
            title: 'Done',
            text: 'Upload initiated and is running on background.',
          })
          this.selectedProductIds = []
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.message || 'Failed to initiate upload.',
          })
        }
      } catch (e) {
      } finally {
        this.initiating_upload = false
      }
    },

    viewAttributeMapping() {
      this.attribute_mapping_dialog = true
      console.log(this.attribute_mapping)
    },

    getParsedPropertyDetails(property_details) {
      try {
        if (typeof property_details === 'string') {
          return JSON.parse(property_details)
        }
        return property_details || {}
      } catch (e) {
        console.error('Error parsing property details:', e)
        return {}
      }
    },

    getParsedValueDetails(value_details) {
      try {
        if (typeof value_details === 'string') {
          return JSON.parse(value_details)
        }
        return value_details || []
      } catch (e) {
        console.error('Error parsing value details:', e)
        return []
      }
    },

    formatDate(date) {
      if (!date) return 'N/A'
      try {
        const dateObj = new Date(date)
        return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString()
      } catch (e) {
        return date
      }
    },

    formatSnakeCase(value) {
      if (!value || typeof value !== 'string') return value

      return value
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    },

    getScaleDisplayName(mapping) {
      try {
        if (mapping.scale) {
          let scaleData = mapping.scale
          if (typeof mapping.scale === 'string') {
            scaleData = JSON.parse(mapping.scale)
          }
          return scaleData.display_name || null
        }
        return null
      } catch (e) {
        console.error('Error parsing scale data:', e)
        return null
      }
    },
    formatOverrideParameters(parameters) {
      if (parameters === null || parameters === undefined) return 'N/A'
      if (typeof parameters === 'string') return parameters
      if (Array.isArray(parameters)) return parameters.join(', ')
      return String(parameters)
    },
    formatPriceOverrideParameters(parameters) {
      if (!parameters || !Array.isArray(parameters)) return 'N/A'
      const [value, type] = parameters
      return `${value} ${type || 'amount'}`
    },
    formatSettingKey(key) {
      // Convert snake_case to Title Case
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    formatSettingValue(value, type) {
      if (value === null || value === undefined) {
        return 'N/A'
      }

      if (type === 'boolean') {
        // Handle string "1" or "0", boolean true/false, or integer 1/0
        const boolValue = value === true || value === 1 || value === '1' || value === 'true'
        return boolValue ? 'Enabled' : 'Disabled'
      }

      if (type === 'integer') {
        // Show integer values as-is (e.g., 0, 1)
        return String(value)
      }

      // For string type, return as-is
      return String(value)
    },

    formatListingTypeValue(value) {
      // is_digital: 0 = physical, 1 = digital
      const intValue = parseInt(value, 10)
      if (intValue === 0) {
        return 'Physical (A tangible item that you will ship to buyers.)'
      } else if (intValue === 1) {
        return 'Digital (A digital file that buyers will download.)'
      }
      return String(value)
    },

    formatRenewalOptionsValue(value) {
      // should_auto_renew: 0 = manual, 1 = automatic
      const intValue = parseInt(value, 10)
      if (intValue === 0) {
        return "Manual (I'll renew expired listings myself.)"
      } else if (intValue === 1) {
        return 'Automatic (This listing will renew as it expires for USD 0.20 each time)'
      }
      return String(value)
    },

    formatWeightUnitValue(value) {
      // weight_unit: 'oz' = Imperial, 'g' = Metric
      const mappings = {
        'oz': 'Imperial System (oz, lb)',
        'g': 'Metric System (g, kg)'
      }
      return mappings[value] || String(value)
    },

    formatDimensionUnitValue(value) {
      // dimension_unit: 'in' = Imperial, 'mm' = Metric
      const mappings = {
        'in': 'Imperial (eg: in, ft, yd)',
        'mm': 'Metric (eg: mm, cm, m)'
      }
      return mappings[value] || String(value)
    },

    formatAdvancedFieldName(key) {
      // Convert snake_case to Title Case
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    formatMapping(mappingString) {
      try {
        if (!mappingString) return 'N/A'
        const mapping = typeof mappingString === 'string' ? JSON.parse(mappingString) : mappingString

        if (mapping.source_type === 'shopify_attribute') {
          return `Shopify Attribute: ${mapping.value || 'N/A'}`
        } else if (mapping.source_type === 'static') {
          return `Static Value: ${mapping.value || 'N/A'}`
        } else if (mapping.source_type === 'custom') {
          return `Custom: ${mapping.value || 'N/A'}`
        }
        return mappingString
      } catch (e) {
        console.error('Error parsing mapping:', e)
        return mappingString
      }
    },
    getProfileOverrideCount() {
      if (!this.profile_override) return 0
      let count = 0
      if (this.profile_override.has_title_overrides) {
        count += this.profile_override.title_overrides.length
      }
      if (this.profile_override.has_price_overrides) {
        count += this.profile_override.price_overrides.length
      }
      return count
    },
    getTotalOverridesCount() {
      let count = 0
      if (this.profile_override) {
        count += this.getProfileOverrideCount()
      }
      if (this.attribute_mapping && this.attribute_mapping.length > 0) {
        count += this.attribute_mapping.length
      }
      if (this.hasTitleOrDescriptionMapping) {
        count += (this.advanced_fields.title_mapping ? 1 : 0) + (this.advanced_fields.description_mapping ? 1 : 0)
      }
      if (this.hasAdvancedFields) {
        const advancedCount = Object.values(this.advanced_fields).filter(
          (value, key) => value && value !== '' && value !== null && key !== 'title_mapping' && key !== 'description_mapping'
        ).length
        count += advancedCount
      }
      return count
    },
    toggleProfileDetails() {
      this.profileDetailsExpanded = !this.profileDetailsExpanded
      // Optionally expand/collapse all child sections when toggling
      if (this.profileDetailsExpanded) {
        // Don't auto-expand children, let user expand individually
      } else {
        // Collapse all children when collapsing wrapper
        this.etsyConfigurationExpanded = false
        this.categoryAttributeMappingExpanded = false
        this.mandatoryProductInfoExpanded = false
        this.productBasicInfoExpanded = false
        this.listingTypeMeasurementExpanded = false
        this.inventorySettingsExpanded = false
        this.additionalSettingsExpanded = false
        this.autoSyncProductDataExpanded = false
        this.profileOverridesExpanded = false
        this.contentMappingsExpanded = false
        this.advancedFieldsExpanded = false
      }
    },
    toggleCollections() {
      this.collectionsExpanded = !this.collectionsExpanded
    },
  },
  computed: {
    productStateTabModel: {
      get() {
        return 'tab-' + this.productStateTab
      },
      set(tabValue) {
        const value = (tabValue || '').replace(/^#?tab-/, '')
        if (value) this.setProductStateTab(value)
      },
    },
    isAllOnPageSelected() {
      const items = this.feed_products || []
      if (!items.length) return false
      return items.every((item) => this.selectedProductIds.indexOf(item.id) !== -1)
    },
    isSomeOnPageSelected() {
      const items = this.feed_products || []
      return items.some((item) => this.selectedProductIds.indexOf(item.id) !== -1)
    },
    hasAdvancedFields() {
      if (!this.advanced_fields) return false
      return Object.values(this.advanced_fields).some(value => value && value !== '' && value !== null)
    },
    hasTitleOrDescriptionMapping() {
      if (!this.advanced_fields) return false
      return !!(this.advanced_fields.title_mapping || this.advanced_fields.description_mapping)
    },
  },
}
</script>

<style scoped>
.product-table-toolbar {
  background-color: #fafafa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.product-search-field {
  max-width: 320px;
}

/* Align row checkbox with header checkbox (match Vuetify data table cell padding) */
.product-table >>> .v-data-table__wrapper table tbody tr td.checkbox-cell {
  padding-left: 16px;
}
.product-table >>> .v-data-table__wrapper table thead th:first-child {
  padding-left: 16px;
}
</style>
